import axiosInstance from '../../api/axios';

// Row types mirror the backend row structs (and their `json` tags) verbatim —
// see skeptics-backend/repository/analytics_reports_repository.go
// (TesterMyControlsRow, TesterDueThisWeekRow, TesterMyProgressRow,
// TesterMyExceptionRow, TesterEvidenceChecklistRow, TesterRerunsRequiredRow).
// Do not rename fields without updating both sides of the contract.
//
// Every Tester report is scoped server-side to the calling user via
// test_runs.assignee_user_id = orgContext.UserID — there is no user_id
// param on any of these fetches.

/** Report #30 — My Assigned Controls. */
export type TesterMyControlsRow = {
  control_id: string;
  title: string;
  frequency: string | null;
  workflow_status: string;
  due_date: string | null;
  testing_period_start: string | null;
  testing_period_end: string | null;
};

/** Report #31 — My Due This Week. */
export type TesterDueThisWeekRow = {
  control_id: string;
  title: string;
  due_date: string | null;
  workflow_status: string;
};

/** Report #32 — My Test Progress. */
export type TesterMyProgressRow = {
  workflow_status: string;
  controls: number;
  passed: number | null;
  failed: number | null;
};

/** Report #33 — My Exception Log (personal variant, assignee-filtered). */
export type TesterMyExceptionRow = {
  control_id: string;
  label: string | null;
  status: string | null;
  deviation_rate: number | null;
  findings_summary: string | null;
  record_ref: string | null;
  observation: string | null;
  severity: string | null;
};

/**
 * Report #34 — Evidence Checklist (per sample, for a given test run). Takes
 * `run_id`, not an assignee filter — see `fetchTesterEvidenceChecklist`.
 */
export type TesterEvidenceChecklistRow = {
  record_ref: string;
  attribute_ref: string | null;
  file_name: string | null;
  source: string | null;
  review_status: string | null;
  confidence: number | null;
};

/** Report #35 in the backend file (docx report #36) — Re-runs Required. */
export type TesterRerunsRequiredRow = {
  control_id: string;
  title: string;
  rerun_reason: string | null;
  workflow_status: string;
  created_at: string | null;
};

/**
 * Report #9 (feedback round 2026-08) — Tester Average Test Time. `avg_days` =
 * AVG(updated_at - created_at) over completed current runs, 1-decimal (v1
 * proxy — precise cycle time needs status-transition timestamps).
 */
export type TesterPerTesterAvgRow = {
  assignee_user_id: number | null;
  tester_name: string;
  avg_days: number;
  tests_completed: number;
};
export type TesterAvgTestTimeResponse = { overall_avg_days: number; per_tester: TesterPerTesterAvgRow[] };

/** Distinct assignees on current runs — feeds the "Tester" slicer (managers slicing by tester). */
export type TesterOption = { user_id: number; name: string };

type RowsEnvelope<T> = { rows: T[] };
type TestersEnvelope = { testers: TesterOption[] };

async function getRows<T>(path: string): Promise<T[]> {
  const response = await axiosInstance.get<RowsEnvelope<T>>(path);
  return response?.data?.rows ?? [];
}

const BASE = '/api/analytics/tester';

/**
 * Appends `?assignee_user_id=<id>` to a Tester report path — every existing
 * Tester report (feedback round 2026-08) accepts this optional filter so a
 * manager can slice by tester; `null`/`undefined` means "no filter" (the
 * caller's own assignment scope, unchanged from before this round).
 */
function withAssignee(path: string, assigneeUserId?: number | null): string {
  return assigneeUserId != null ? `${path}?assignee_user_id=${encodeURIComponent(assigneeUserId)}` : path;
}

export const fetchTesterMyControls = (assigneeUserId?: number | null): Promise<TesterMyControlsRow[]> =>
  getRows<TesterMyControlsRow>(withAssignee(`${BASE}/my-controls`, assigneeUserId));

export const fetchTesterDueThisWeek = (assigneeUserId?: number | null): Promise<TesterDueThisWeekRow[]> =>
  getRows<TesterDueThisWeekRow>(withAssignee(`${BASE}/due-this-week`, assigneeUserId));

export const fetchTesterMyProgress = (assigneeUserId?: number | null): Promise<TesterMyProgressRow[]> =>
  getRows<TesterMyProgressRow>(withAssignee(`${BASE}/my-progress`, assigneeUserId));

export const fetchTesterMyExceptions = (assigneeUserId?: number | null): Promise<TesterMyExceptionRow[]> =>
  getRows<TesterMyExceptionRow>(withAssignee(`${BASE}/my-exceptions`, assigneeUserId));

/**
 * Report #34 requires a `run_id` (UUID) query param — none of the other
 * Tester reports expose a run id in their row shape (TesterMyControlsRow /
 * TesterRerunsRequiredRow only carry control_id), so the caller must supply
 * one explicitly (e.g. from a run-id input/picker in the UI).
 */
export const fetchTesterEvidenceChecklist = (runId: string): Promise<TesterEvidenceChecklistRow[]> =>
  getRows<TesterEvidenceChecklistRow>(`${BASE}/evidence-checklist?run_id=${encodeURIComponent(runId)}`);

export const fetchTesterRerunsRequired = (assigneeUserId?: number | null): Promise<TesterRerunsRequiredRow[]> =>
  getRows<TesterRerunsRequiredRow>(withAssignee(`${BASE}/reruns-required`, assigneeUserId));

export const fetchTesterAvgTestTime = async (): Promise<TesterAvgTestTimeResponse> => {
  const response = await axiosInstance.get<TesterAvgTestTimeResponse>(`${BASE}/avg-test-time`);
  return response?.data ?? { overall_avg_days: 0, per_tester: [] };
};

export const fetchTesterTesters = async (): Promise<TesterOption[]> => {
  const response = await axiosInstance.get<TestersEnvelope>(`${BASE}/testers`);
  return response?.data?.testers ?? [];
};
